import { useState, useEffect, useCallback } from 'react';
import { communityApi } from './communityApi';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { BarChart3, HelpCircle, TrendingUp, Bell, Loader2, Heart, MessageCircle, Send, ChevronDown, ChevronUp } from 'lucide-react';

const FILTER_OPTIONS = [
  { key: 'all', label: 'Tout' },
  { key: 'theses', label: 'Thèses' },
  { key: 'questions', label: 'Questions' },
  { key: 'results', label: 'Résultats' },
];

const typeConfig = {
  thesis: { icon: BarChart3, color: 'text-[#4f8eff]', bg: 'bg-[#4f8eff]/10', label: 'Thèse' },
  question: { icon: HelpCircle, color: 'text-amber-400', bg: 'bg-amber-400/10', label: 'Question' },
  result: { icon: TrendingUp, color: 'text-[#00e5a0]', bg: 'bg-[#00e5a0]/10', label: 'Résultat' },
  activity: { icon: Bell, color: 'text-purple-400', bg: 'bg-purple-400/10', label: 'Activité' },
};

const MiniAvatar = ({ username }) => {
  const colors = ['from-emerald-500/40 to-emerald-900/40', 'from-blue-500/40 to-blue-900/40', 'from-purple-500/40 to-purple-900/40', 'from-amber-500/40 to-amber-900/40'];
  const idx = (username || '').charCodeAt(0) % colors.length;
  return (
    <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${colors[idx]} flex items-center justify-center font-display font-bold text-white text-xs border border-white/10 flex-shrink-0`}>
      {(username || '?')[0]}
    </div>
  );
};

const timeSince = (dateStr) => {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return "à l'instant";
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return days < 7 ? `${days}j` : new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

// ── Thesis Comments inline (mini version for feed) ──
const FeedComments = ({ thesisId, count }) => {
  const [expanded, setExpanded] = useState(false);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setComments(await communityApi.getComments(thesisId)); } catch { /* */ }
    finally { setLoading(false); }
  }, [thesisId]);

  useEffect(() => { if (expanded) load(); }, [expanded, load]);

  const handleSubmit = async () => {
    if (!newComment.trim()) return;
    setSending(true);
    try {
      const c = await communityApi.addComment(thesisId, newComment.trim());
      setComments(prev => [...prev, c]);
      setNewComment('');
      toast.success('+10 XP — Commentaire ajouté !');
    } catch { toast.error("Erreur"); }
    finally { setSending(false); }
  };

  return (
    <div>
      <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-1 text-cap-text-muted text-[10px] hover:text-[#4f8eff] transition-colors">
        <MessageCircle className="w-3 h-3" /> {count} {expanded ? <ChevronUp className="w-2.5 h-2.5" /> : <ChevronDown className="w-2.5 h-2.5" />}
      </button>
      {expanded && (
        <div className="mt-2 pt-2 border-t border-white/5 space-y-2">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin text-cap-text-muted mx-auto" /> :
            comments.filter(c => !c.parent_id).slice(0, 3).map(c => (
              <div key={c.id} className="flex gap-2">
                <div className={`w-6 h-6 rounded-full bg-gradient-to-br from-blue-500/40 to-blue-900/40 flex items-center justify-center text-[9px] font-bold text-white border border-white/10 flex-shrink-0`}>{(c.author||'?')[0]}</div>
                <div><span className="text-white text-[11px] font-semibold mr-1">{c.author}</span><span className="text-cap-text-secondary text-[11px]">{c.content}</span></div>
              </div>
            ))}
          <div className="flex gap-1.5 mt-1">
            <input className="flex-1 bg-[#0d0f14] border border-white/10 rounded-md px-2 h-7 text-white text-[11px] outline-none placeholder:text-cap-text-muted focus:border-[#00e5a0]/40"
              placeholder="Commenter..." value={newComment} onChange={e => setNewComment(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSubmit()} />
            <button onClick={handleSubmit} disabled={!newComment.trim() || sending}
              className="h-7 w-7 rounded-md bg-[#00e5a0] flex items-center justify-center text-[#0d0f14] disabled:opacity-40">
              <Send className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const FeedItem = ({ item, onLike }) => {
  const cfg = typeConfig[item.type] || typeConfig.activity;
  const Icon = cfg.icon;

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="bg-[#1a1d27] border border-white/5 rounded-xl p-4 flex gap-3" data-testid={`feed-${item.type}-${item.id}`}>
      <MiniAvatar username={item.author} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-white text-sm font-semibold">{item.author}</span>
          <span className={`text-[9px] px-1.5 py-0.5 rounded ${cfg.bg} ${cfg.color} font-medium flex items-center gap-0.5`}>
            <Icon className="w-2.5 h-2.5" /> {cfg.label}
          </span>
          <span className="text-cap-text-muted text-[10px]">{timeSince(item.created_at)}</span>
        </div>

        {item.type === 'thesis' && (
          <>
            <p className="text-white text-sm font-medium">{item.title}</p>
            {item.symbol && <span className="font-mono text-[#00e5a0] text-xs">${item.symbol}</span>}
            <p className="text-cap-text-secondary text-xs mt-1 line-clamp-2">{item.summary}</p>
            <div className="flex items-center gap-3 mt-2">
              {(item.tags || []).map(t => <span key={t} className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/5 text-cap-text-muted">#{t}</span>)}
            </div>
            {/* Functional like + comments */}
            <div className="flex items-center gap-4 mt-2">
              <button onClick={() => onLike(item.id)}
                className={`flex items-center gap-1 text-[10px] transition-colors ${item.user_liked ? 'text-red-400' : 'text-cap-text-muted hover:text-red-400'}`}
                data-testid={`feed-like-${item.id}`}>
                <Heart className={`w-3.5 h-3.5 ${item.user_liked ? 'fill-red-400' : ''}`} /> {item.likes_count || 0}
              </button>
              <FeedComments thesisId={item.id} count={item.comments_count || 0} />
            </div>
          </>
        )}

        {item.type === 'question' && (
          <>
            <p className="text-white text-sm">{item.content}</p>
            <div className="flex items-center gap-2 mt-1.5">
              {item.symbol && <span className="font-mono text-[#00e5a0] text-xs bg-[#00e5a0]/10 px-1.5 py-0.5 rounded">${item.symbol}</span>}
              <span className="text-cap-text-muted text-[10px] flex items-center gap-0.5"><MessageCircle className="w-3 h-3" /> {item.answers_count || 0} réponse{(item.answers_count || 0) !== 1 ? 's' : ''}</span>
            </div>
          </>
        )}

        {item.type === 'result' && (
          <div className="flex items-center gap-4 mt-1">
            <p className={`font-display text-lg font-bold ${item.month_perf >= 0 ? 'text-[#00e5a0]' : 'text-red-400'}`}>
              {item.month_perf >= 0 ? '+' : ''}{item.month_perf}%
            </p>
            <p className={`font-display text-sm ${item.ytd_perf >= 0 ? 'text-white/60' : 'text-red-400/60'}`}>
              YTD: {item.ytd_perf >= 0 ? '+' : ''}{item.ytd_perf}%
            </p>
            {item.verification_status === 'verified' && <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400">Vérifié</span>}
          </div>
        )}
      </div>
    </motion.div>
  );
};

const FeedTab = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems(await communityApi.getFeed(filter)); }
    catch { /* */ } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const handleLike = async (thesisId) => {
    try {
      const r = await communityApi.toggleLike(thesisId);
      setItems(prev => prev.map(i =>
        i.type === 'thesis' && i.id === thesisId ? { ...i, likes_count: r.likes_count, user_liked: r.liked } : i
      ));
    } catch { /* */ }
  };

  return (
    <div className="space-y-4" data-testid="feed-tab">
      <div className="flex items-center gap-2">
        {FILTER_OPTIONS.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`text-xs px-3 py-1.5 rounded-full transition-all ${filter === f.key ? 'bg-[#00e5a0]/20 text-[#00e5a0] font-bold' : 'bg-white/5 text-cap-text-secondary hover:bg-white/10'}`}>
            {f.label}
          </button>
        ))}
      </div>

      {loading && <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cap-text-muted" /></div>}
      {!loading && items.length === 0 && <p className="text-cap-text-muted text-sm text-center py-12">Le fil est vide pour le moment.</p>}

      <div className="space-y-5">
        {items.map(item => <FeedItem key={`${item.type}-${item.id}`} item={item} onLike={handleLike} />)}
      </div>
    </div>
  );
};

export default FeedTab;
